import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { handleFileDownload } from './download-dropdown'
import { Icon, Spinner } from '../../atoms'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../../shadcn/components/ui/dropdown-menu'
import { cn } from '../../../shadcn/utils'

export interface FileDownloadDropdownProps {
  files: { url: string; name: string }[]
  isDisabled?: boolean
  className?: string
}

export const DownloadDropdown = ({
  files,
  isDisabled,
  className,
}: FileDownloadDropdownProps) => {
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false)

  const handleDownloadAll = () => {
    files.forEach((file, index) => {
      if (!file?.url || !file?.name) {
        console.error('Skipping invalid file during download all:', file)
        return
      }

      // the delay is needed
      setTimeout(() => {
        handleFileDownload(file)
      }, index * 500)
    })
  }

  return (
    <DropdownMenu
      modal={false}
      onOpenChange={(open) => setDropdownIsOpen(open)}
    >
      <DropdownMenuTrigger
        className={cn(
          'flex h-10 w-full max-w-[164px] items-center rounded-md border border-solid border-foreground bg-white px-4 py-2 font-medium',
          className,
        )}
        disabled={isDisabled}
      >
        <>
          {!files || files.length < 0 ? (
            <>
              <p>
                <FormattedMessage
                  id='ui.DownloadPdfDropdown.generateDocs'
                  defaultMessage='Génération des documents'
                />
              </p>
              <Spinner color='gradient' width={14} height={14} />
            </>
          ) : (
            <div className='flex w-full items-center gap-1'>
              <p className='overflow-hidden text-ellipsis whitespace-nowrap text-sm'>
                <FormattedMessage
                  id='ui.DownloadDropdown.downloadDocs'
                  defaultMessage='Télécharger'
                />
              </p>
              {isDisabled ? (
                <Icon name='lock' className='ml-auto' />
              ) : (
                <div className='ml-auto flex items-center'>
                  <Icon name='paperclip' className='h-[14px] w-[14px]' />
                  <p>{files.length.toString()}</p>
                  <Icon
                    name='arrow-down'
                    size={12}
                    className={cn('ml-3 transition ease-in-out', {
                      ['rotate-180']: dropdownIsOpen,
                    })}
                  />
                </div>
              )}
            </div>
          )}
        </>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className='max-h-80 w-[--radix-popper-anchor-width] max-w-full overflow-y-auto'
        side='bottom'
      >
        <div className='shadow-black/20 shadow-md'>
          <ul className='divide-y divide-grey-400'>
            {files.map((file, index) => (
              <li key={index}>
                <button
                  className='group flex w-full cursor-pointer items-center gap-1 px-6 py-4'
                  onClick={() => handleFileDownload(file)}
                >
                  <Icon name='paperclip' size={16} />
                  <span className='flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left text-sm group-hover:underline'>
                    {file.name}
                  </span>
                </button>
              </li>
            ))}
            <li>
              <button
                className='group flex w-full cursor-pointer items-center gap-1 px-6 py-4 text-sm hover:underline'
                onClick={handleDownloadAll}
              >
                <FormattedMessage
                  id='ui.DownloadPdfDropdown.downloadAllDocs'
                  defaultMessage='Tout enregistrer'
                />
              </button>
            </li>
          </ul>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
